import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const Step = ({ n, text }) => (
  <div className="flex gap-3">
    <div className="w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{n}</div>
    <p className="text-sm text-gray-700 dark:text-gray-300">{text}</p>
  </div>
);

const QA = ({ q, a }) => (
  <div>
    <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Q. {q}</p>
    <p className="text-sm text-gray-700 dark:text-gray-300">→ {a}</p>
  </div>
);

const sections = [
  {
    title: '처음 시작하기',
    content: (
      <div className="space-y-3">
        <Step n={1} text="설정 페이지에서 헬스장 이름, 영업시간 등 기본 정보를 입력하세요." />
        <Step n={2} text="설정 페이지 하단에서 매장 코드를 확인하세요. 회원들이 앱 가입 시 사용합니다." />
        <Step n={3} text="회원권 관리 → 상품 탭에서 이용할 회원권 상품을 먼저 등록하세요." />
        <Step n={4} text="회원 관리에서 첫 회원을 등록하거나 회원 앱 가입 신청을 승인하세요." />
      </div>
    ),
  },
  {
    title: '회원 관리',
    content: (
      <div className="space-y-3">
        <QA q="회원을 직접 등록하려면?" a="회원 관리 → 우측 상단 '+ 회원 등록' 버튼을 누르세요." />
        <QA q="회원이 앱으로 가입 신청했어요" a="알림 센터에서 가입 신청을 확인하고 승인/거절하세요." />
        <QA q="회원 정보를 수정하려면?" a="회원 목록에서 회원 클릭 → 수정 버튼을 누르세요." />
        <QA q="회원이 정보 수정을 요청했어요" a="알림 센터 → 정보 수정 신청 탭에서 확인하세요." />
      </div>
    ),
  },
  {
    title: '회원권 & PT 관리',
    content: (
      <div className="space-y-3">
        <QA q="회원권을 부여하려면?" a="회원 상세 페이지 → 회원권 탭 → '+ 회원권 부여'" />
        <QA q="회원권을 일시정지하려면?" a="회원 상세 → 회원권 탭 → 해당 회원권 → '일시정지'" />
        <QA q="연장 신청이 들어왔어요" a="알림 센터 → 연장 신청 탭에서 확인 후, 결제 받고 회원권 직접 연장 후 승인하세요." />
        <QA q="PT를 등록하려면?" a="회원 상세 → PT 탭 → '+ PT 부여'" />
      </div>
    ),
  },
  {
    title: '출석 관리',
    content: (
      <div className="space-y-3">
        <QA q="출석을 수동으로 체크하려면?" a="대시보드 빠른 출석 체크 또는 출석 관리 페이지에서 이름 검색 후 체크" />
        <QA q="QR 키오스크를 사용하려면?" a="출석 관리 페이지 우측 상단 'QR 키오스크' 버튼 → 카메라로 회원 QR 스캔" />
        <QA q="회원 QR코드는 어디서 받나요?" a="회원이 앱 하단 'QR' 탭에서 확인할 수 있습니다." />
      </div>
    ),
  },
  {
    title: '매출 & 통계',
    content: (
      <div className="space-y-3">
        <QA q="매출을 기록하려면?" a="매출 관리 페이지에서 수입/지출 항목을 직접 입력하세요." />
        <QA q="회원 현황을 한눈에 보려면?" a="통계/분석 페이지에서 회원, 출석, 매출 통계를 확인하세요." />
      </div>
    ),
  },
  {
    title: '트레이너 관리',
    content: (
      <div className="space-y-3">
        <QA q="트레이너를 추가하려면?" a="트레이너 관리 → '+ 트레이너 등록' → 이름과 전화번호 입력 후 등록" />
        <QA q="트레이너에게 앱 초대링크를 보내려면?" a="트레이너 목록에서 해당 트레이너 → '초대 링크 발급' 버튼 → 링크를 복사해 전달하세요." />
        <QA q="트레이너가 링크로 가입하면?" a="트레이너가 이메일과 비밀번호를 설정해 가입합니다. 이후 이메일+비밀번호로 로그인합니다." />
      </div>
    ),
  },
  {
    title: '알림 센터 활용법',
    content: (
      <div className="space-y-3">
        <p className="text-sm text-gray-700 dark:text-gray-300">알림 센터에는 3가지 신청이 모입니다: 가입 신청, 연장 신청, 정보 수정 신청</p>
        <p className="text-sm text-gray-700 dark:text-gray-300">사이드바 알림 아이콘의 숫자 뱃지 = 처리 대기 중인 신청 수</p>
        <p className="text-sm text-gray-700 dark:text-gray-300">각 신청을 클릭하면 상세 내용을 확인하고 승인 또는 거절할 수 있습니다.</p>
      </div>
    ),
  },
  {
    title: '요금제 & 납부',
    content: (
      <div className="space-y-3">
        <QA q="요금제를 연장하려면?" a="설정 → 요금제 납부 탭 → 납부할 금액 선택 후 계좌이체 → 납부 요청 등록. 운영사 확인 후 플랜이 연장됩니다." />
        <QA q="납부 계좌는?" a="국민은행 703001-01-598295 (티앤티 주식회사)" />
        <QA q="월 정기권 vs 연간 결제?" a="월 정기권 49,000원 / 연간 결제 490,000원 (2개월 무료)" />
        <QA q="무료 체험 기간이 끝나면?" a="플랜이 만료되면 서비스 이용이 제한됩니다. 설정 페이지에서 납부 요청을 등록해주세요." />
      </div>
    ),
  },
];

export default function Help() {
  const [openSection, setOpenSection] = useState(0);

  return (
    <div className="max-w-2xl mx-auto space-y-3">
      {sections.map((section, idx) => {
        const isOpen = openSection === idx;
        return (
          <div
            key={idx}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            <button
              onClick={() => setOpenSection(isOpen ? -1 : idx)}
              className="w-full flex items-center justify-between px-5 py-4 text-left"
            >
              <span className="font-semibold text-gray-900 dark:text-white text-sm">{section.title}</span>
              <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {isOpen && (
              <div className="px-5 pb-5 border-t border-gray-100 dark:border-gray-700 pt-4">
                {section.content}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
